import { useRouter } from 'expo-router';
import { ComponentProps } from 'react';
import { XStack, YStack } from 'tamagui';
import PSIcon from './ui/PSIcon';
import PSLinkButton from './ui/PSLinkButton';
import PSText from './ui/PSText';

/**
 * UserDetails — the "Your details" box on the profile screen (molecule).
 *
 * A filled ($color.background, $base radius, $md pad) box listing the signed-in
 * user's contact: name, email, mobile — each on its own line, no captions.
 *
 * Render-only. `name`/`email` are nullable and null-guarded here on purpose:
 * a session starts at mobile-verify alone (see the auth flow), and email is
 * now deferred to booking time, so "logged in" no longer implies either is
 * present — only `mobile` is guaranteed.
 */
export default function UserDetails({
  name,
  email,
  mobile,
  socialMediaLink,
  areDetailsMissing,
  isEmailMissing,
  isOnlyEmailVerificationPending,
}: {
  name: string | null;
  email: string | null;
  mobile: string;
  socialMediaLink?: string;
  areDetailsMissing: boolean;
  isEmailMissing: boolean;
  isOnlyEmailVerificationPending: boolean;
}) {
  const router = useRouter();
  return (
    <YStack
      borderWidth="$borderWidth.hairline"
      borderColor="$borderColor"
      rounded="$base"
      p="$md"
      gap="$md"
    >
      {name ? <UserDetailsLine value={name} iconName="person-outline" /> : null}
      {email ? <UserDetailsLine value={email} iconName="mail-outline" /> : null}
      <UserDetailsLine value={mobile} iconName="call-outline" />
      {socialMediaLink ? (
        <XStack alignItems="center" gap={'$space.sm'}>
          <PSIcon name={'link-outline'} size={16} />
          <PSText.Body2
            flex={1}
            minWidth={0}
            color="$textDark"
            textDecorationLine="underline"
          >
            {socialMediaLink}
          </PSText.Body2>
        </XStack>
      ) : null}

      {areDetailsMissing && (
        <PSText.Body2 mt={'$space.md'}>
          Please{' '}
          <PSLinkButton
            title="complete your profile"
            onPress={() =>
              router.push({
                pathname: '/auth/details',
                params: { headerCopy: 'Complete your profile', next: 'email' },
              })
            }
          />{' '}
          to be able to book tickets.
        </PSText.Body2>
      )}
      {isEmailMissing && (
        <PSText.Body2 mt={'$space.md'}>
          Please{' '}
          <PSLinkButton
            title="add and verify your email"
            onPress={() =>
              router.push({
                pathname: '/auth/email',
                params: { headerCopy: 'Add and verify your email' },
              })
            }
          />{' '}
          to be able to book tickets.
        </PSText.Body2>
      )}
      {isOnlyEmailVerificationPending && (
        <PSText.Body2 mt={'$space.md'}>
          Please{' '}
          <PSLinkButton
            title="verify your email"
            onPress={() =>
              router.push({
                pathname: '/auth/email',
                params: { headerCopy: 'Verify your email' },
              })
            }
          />{' '}
          to be able to book tickets.
        </PSText.Body2>
      )}
    </YStack>
  );
}

/** One value line — kept to one line so a long email/name ellipsizes rather
 * than wrapping the box taller. */
export function UserDetailsLine({
  iconName,
  value,
}: {
  iconName: ComponentProps<typeof PSIcon>['name'];
  value: string;
}) {
  return (
    <XStack alignItems="center" gap={'$space.sm'}>
      <PSIcon name={iconName} size={16} />
      <PSText.Body2 flex={1} color="$textDark" numberOfLines={1}>
        {value}
      </PSText.Body2>
    </XStack>
  );
}
